<script setup lang="ts">
import { calculateValidity, stringToFaDateTime } from '@payjib/shared'
import { computed } from 'vue'
import type { Transaction } from '@/api/typings/transaction.typing'
import { useGeneralSettings } from '@/composables/useGeneralSettings'
import { useToast } from '@/composables/useToast'
import { hasPendingCancellation } from '@/utils/has-pending-cancellation.util'
import { numberWithCommas } from '@/utils/number-with-commas'

const { transaction } = defineProps<{
  transaction: Transaction | undefined
}>()

const toast = useToast()
const { transactionsStatusOptions } = useGeneralSettings()
const status = computed(() => {
  const status = transaction?.status

  switch (status) {
    case 'INITIAL_OWNER_CONFIRMATION_PENDING':
    case 'BUYER_PAYMENT_PENDING':
    case 'BUYER_PAYMENT_VERIFICATION_PENDING':
    case 'SELLER_PAYMENT_PENDING':
    case 'SELLER_PAYMENT_VERIFICATION_PENDING':
    case 'SETTLE_WITH_SELLER_PENDING':
      return {
        label: 'در حال انجام',
        class: 'pending',
      }

    case 'CANCELLED':
      return {
        label: 'لغو شده',
        class: 'canceled',
      }
    case 'TRANSFERRED':
      return {
        label: 'منتقل شده',
        class: 'transferred',
      }

    case 'BUYER_REJECTED':
    case 'SELLER_REJECTED':
      return {
        label: status === 'BUYER_REJECTED' ? ' عدم پذیرش توسط خریدار' : ' عدم پذیرش توسط فروشنده',
        class: 'rejected',
      }

    case 'FINISHED':
      return {
        label: 'موفق',
        class: 'success',
      }

    default:
      return {
        label: 'موفق',
        class: 'success',
      }
  }
})
const statusLabel = computed(() => {
  const s = transaction?.status
  if (!s) return null
  return transactionsStatusOptions.value.find((o) => o.value === s)?.label ?? null
})
const isCancellationPending = computed(() => hasPendingCancellation(transaction?.requisition?.cancellationRequests))
const copyText = async (text: string | undefined, successMessage: string) => {
  if (!text) return
  try {
    await navigator.clipboard.writeText(text)
    toast.success(successMessage)
  } catch (error) {
    toast.error('کپی کردن با خطا مواجه شد', 'خطا')
  }
}
</script>

<template>
  <div class="flex flex-col gap-6 bg-white rounded-xl p-6 text- text-black font-medium">
    <!-- Header -->
    <div class="flex items-center justify-center lg:justify-between lg:pe-1">
      <div class="w-full lg:w-fit flex flex-col lg:flex-row items-center gap-2 lg:gap-[5px]">
        <div class="w-full lg:w-auto flex items-center justify-center font-medium text-[13px] h-10 rounded-[10px] px-4"
          :class="{
            'bg-[#eaeef5] text-[#2d5396]': status.class === 'pending',
            'bg-[#e6fff0] text-[#2ed06e]': status.class === 'success',
            'bg-[#fff8e7] text-[#ffba0e]': status.class === 'canceled',
            'bg-[#fde7ef] text-[#ed145b]': status.class === 'rejected',
            'bg-[#fff4e5] text-[#b86b00]': status.class === 'transferred',
          }">
          <span class="font-fa ps-1">{{ transaction?.code }}</span>
          <span class="mx-2">|</span>
          {{ status.label }}
        </div>

        <div v-if="transaction?.discount?.currentDiscount"
          class="w-full lg:w-auto flex items-center bg-[#f3d0f2] text-[#8c228a] justify-center font-medium text-[13px] h-10 rounded-[10px] px-5">
          دارای تخفیف
        </div>
        <div v-if="statusLabel"
          class="w-full lg:w-auto flex items-center justify-center font-medium text-[13px] h-10 rounded-[10px] px-4 bg-[#eaeef5] text-[#2d5396]">
          {{ statusLabel }}
        </div>
        <div v-if="isCancellationPending"
          class="w-full lg:w-auto flex items-center justify-center font-medium text-[13px] h-10 rounded-[10px] px-4 bg-[#fff1e6] text-[#d9480f]">
          در انتظار بررسی لغو
        </div>
      </div>

      <RouterLink :to="`/transactions/${transaction?.uuid}`" class="hidden lg:block">
        <Button class="px-2 h-10! py-0 text-[13px] font-normal" :isAdmin="true">
          مشاهده
        </Button>
      </RouterLink>
    </div>
    <!-- Body -->
    <div class="flex flex-col gap-3 text-sm font-medium text-[#000000]">
      <div class="flex items-center gap-1 ">
        <span>درخواست {{ transaction?.requisition?.type === 'BUY' ? 'خرید' : 'فروش' }}</span>
        <span>
          <span class="font-fa">{{ numberWithCommas(transaction?.currencyAmount) }}</span>
          {{ transaction?.currency?.titleFa }} -
          {{ transaction?.requisition?.bankAccountCountries?.seller?.title?.fa }}

          {{ transaction?.requisition?.bankAccountCountries?.internationalRevolutBuyer?.title?.fa ||
            transaction?.requisition?.bankAccountCountries?.internationalPaypalBuyer?.title?.fa ||
            transaction?.requisition?.bankAccountCountries?.internationalBuyer?.title?.fa ||
            transaction?.requisition?.bankAccountCountries?.internationalWiseBuyer?.title?.fa}}
        </span>
      </div>
      <div class="flex items-center gap-1 ">
        <span>نرخ تبدیل:</span>
        <span class="font-fa">{{ numberWithCommas(transaction?.exchangeRate) }}
        </span>تومان
      </div>
      <div class="flex items-center gap-1 ">
        <span>خریدار:</span>
        <RouterLink v-if="transaction?.buyer?.id" :to="`/users/${transaction.buyer.id}`"
          class="text-[#2d5396] hover:underline">
          {{ transaction?.buyer?.fullName }}
        </RouterLink>
        <span v-else>{{ transaction?.buyer?.fullName }}</span>
        <span class="font-fa">
          ({{ transaction?.buyer?.code }})</span>
        <button v-if="transaction?.buyer?.fullName" type="button"
          @click="copyText(transaction?.buyer?.fullName, 'نام خریدار کپی شد')"
          class="p-1 hover:bg-gray-100 rounded-md transition-colors" title="کپی نام خریدار">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        </button>
      </div>
      <div class="flex items-center gap-1 ">
        <span>فروشنده:</span>
        <RouterLink v-if="transaction?.seller?.id" :to="`/users/${transaction.seller.id}`"
          class="text-[#2d5396] hover:underline">
          {{ transaction?.seller?.fullName }}
        </RouterLink>
        <span v-else>{{ transaction?.seller?.fullName }}</span>
        <span class="font-fa">
          ({{ transaction?.seller?.code }})</span>
        <button v-if="transaction?.seller?.fullName" type="button"
          @click="copyText(transaction?.seller?.fullName, 'نام فروشنده کپی شد')"
          class="p-1 hover:bg-gray-100 rounded-md transition-colors" title="کپی نام فروشنده">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        </button>
      </div>
    </div>

    <div
      class="flex flex-col lg:flex-row lg:items-end gap-6 mt-2 lg:-mt-2 lg:gap-0 justify-end pe-1 text-sm text-[#1b325a] font-normal">

      <!-- Dates -->
      <div class="flex flex-wrap items-center justify-between lg:justify-end text-sm font-medium gap-1 mb-2">
        <div class="flex items-center">
          تاریخ ثبت: <span dir="ltr" class="ms-1 font-fa">{{ transaction?.createdAt ?
            stringToFaDateTime(transaction?.createdAt) : '-'
            }}</span>
        </div>
        <div class="hidden lg:block mx-2 border-e border-[#D5DDEA] w-1 h-5"></div>
        <div class="flex items-center">
          زمان آخرین تغییرات:
          <span class="font-fa mx-1"> {{ transaction?.updatedAt ? calculateValidity(transaction?.updatedAt) : '-'
          }}</span>
        </div>

      </div>

    </div>
    <!-- Description -->
    <div v-if="transaction?.requisition?.description"
      class="border-t border-[#eaeef5] pt-4 text-[13px] text-[#4e648c] font-medium -mt-2">
      توضیحات: {{ transaction?.requisition?.description }}
    </div>
    <RouterLink :to="`/transactions/${transaction?.uuid}`" class="block lg:hidden">
      <Button class="px-2 h-10! py-0 text-[13px] font-normal" :isAdmin="true">
        مشاهده
      </Button>
    </RouterLink>

  </div>
</template>
